import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AxiosError } from 'axios';
import { FormikValues } from 'formik';
import { GridColumnVisibilityModel, GridColDef } from '@mui/x-data-grid';
import { ActionButtons } from '@appTypes';
import { DataGrid } from '@components/Common.styles';
import { Filters } from '@components/Filters';
import { NoRowsOverlayCustom } from './components/NoRowsOverlayCustom';
import { TableType } from './enums';
import { defaultPageSizeOptions } from './helpers/constants';
import { filterDataByTextMatch } from './helpers/filterDataByTextMatch';
import { TableWrapper } from './TableWrapper';
import { ClientSideFilterableDataList, IFilterSettings } from './types';
import { processTableData } from './utils';

interface IClientTableProps<TFilterValues extends FormikValues> {
  buttons?: ActionButtons;
  columnVisibility?: GridColumnVisibilityModel;
  columns: GridColDef[];
  data: object[];
  error?: AxiosError | null;
  filterSettings?: IFilterSettings<TFilterValues>;
  isInTab?: boolean;
  isLoading?: boolean;
  minWidth?: string;
  name: string;
  title?: string;
  wrapTableInCard?: boolean;
}

export const ClientTableV2 = <TFilterValues extends FormikValues>({
  buttons = [],
  columns = [],
  columnVisibility = {},
  data = [],
  error,
  filterSettings,
  isInTab = false,
  isLoading,
  minWidth,
  name,
  title,
  wrapTableInCard = true,
}: IClientTableProps<TFilterValues>) => {
  const getRowHeight = useCallback(() => 'auto', []);
  const processedData = useMemo(() => processTableData(data), [data]);
  const [filteredData, setFilteredData] = useState(processedData);

  const {
    filterHandler,
    filters,
    filterValues,
    initialDefaultValues,
    resetFiltersHandler,
    showClearFiltersButton = true,
  } = filterSettings ?? {};

  const hasInitialFilterApplied = useRef(false);

  useEffect(() => {
    if (
      !hasInitialFilterApplied.current &&
      initialDefaultValues &&
      filterHandler
    ) {
      filterHandler(initialDefaultValues);
      hasInitialFilterApplied.current = true;
    }
  }, [initialDefaultValues, filterHandler]);

  useEffect(() => {
    const result = filterDataByTextMatch(
      processedData as ClientSideFilterableDataList,
      filterValues ?? {},
    );
    setFilteredData(result);
  }, [filterValues, processedData]);

  const handleFilterReset = useCallback(() => {
    resetFiltersHandler?.();
  }, [resetFiltersHandler]);

  const handleFilterChange = useCallback(
    (changedFilterValues: TFilterValues) => {
      filterHandler?.(changedFilterValues);
    },
    [filterHandler],
  );

  return (
    <TableWrapper
      buttons={buttons}
      error={error}
      isLoading={isLoading}
      title={title}
      wrapTableInCard={wrapTableInCard}
    >
      {filters ? (
        <Filters
          filterHandler={handleFilterChange}
          filters={filters}
          isInTab={isInTab}
          resetFilters={handleFilterReset}
          showClearFiltersButton={showClearFiltersButton}
        />
      ) : null}
      <DataGrid
        aria-label={name}
        autoHeight={true}
        clipboardCopyCellDelimiter=","
        columnHeaderHeight={56}
        columns={columns}
        disableColumnMenu={true}
        disableRowSelectionOnClick={true}
        getRowHeight={getRowHeight}
        initialState={{
          columns: {
            columnVisibilityModel: columnVisibility,
          },
          pagination: {
            paginationModel: {
              pageSize: 10,
            },
          },
        }}
        minWidth={minWidth}
        pageSizeOptions={defaultPageSizeOptions}
        pagination={true}
        paginationMode={TableType.CLIENT}
        rows={filters ? filteredData : processedData}
        slots={{
          noRowsOverlay: NoRowsOverlayCustom,
        }}
      />
    </TableWrapper>
  );
};
